import axios from 'axios';
import { mockProjects, mockChatMessages, mockTimelineData, mockTranscription } from '../mock/mockData';

// Local development backend URL
const BACKEND_URL = "http://localhost:8001";
const API = `${BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.baseURL = API;
axios.defaults.withCredentials = true; // Enable credentials for CORS
axios.defaults.headers.common['Content-Type'] = 'application/json';

// Add auth token to requests
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Ensure credentials are included
  config.withCredentials = true;
  return config;
});

// Authentication API
export const authAPI = {
  // Login user
  login: async (email, password) => {
    try {
      const response = await axios.post('/auth/login', {
        email,
        password
      }, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },

  // Register user
  register: async (email, password, full_name) => {
    try {
      const response = await axios.post('/auth/register', {
        email,
        password,
        full_name
      }, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  },

  // Get current user
  getCurrentUser: async () => {
    try {
      const response = await axios.get('/auth/me', {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Get current user error:', error);
      throw error;
    }
  },

  // Logout user
  logout: async () => {
    try {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  }
};

// Video Project API
export const projectAPI = {
  // Create new project
  createProject: async (projectData) => {
    try {
      const response = await axios.post('/projects', projectData);
      return response.data;
    } catch (error) {
      console.error('Error creating project (using fallback):', error);
      // Fallback mock project with proper structure for AITimelineEditor
      const mockProject = {
        id: 'new-project-' + Date.now(),
        name: projectData.name || 'New Project',
        type: projectData.type || 'short-form',
        status: 'created',
        created_at: new Date().toISOString(),
        clips: [], // Empty clips array for new project
        duration: 0,
        aspectRatio: projectData.type === 'short-form' ? '9:16' : '16:9',
        isNew: true
      };
      console.log('Created fallback project:', mockProject);
      return mockProject;
    }
  },

  // Upload video clip to project
  uploadClipToProject: async (projectId, file, order = 0) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('order', order.toString());
      
      const response = await axios.post(`/projects/${projectId}/clips`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading clip:', error);
      return {
        id: 'clip-' + Date.now(),
        project_id: projectId,
        order: order,
        status: 'uploaded'
      };
    }
  },

  // Start project processing (stitching, transcription)
  startProjectProcessing: async (projectId) => {
    try {
      const response = await axios.post(`/projects/${projectId}/process`);
      return response.data;
    } catch (error) {
      console.error('Error starting project processing:', error);
      return {
        status: 'processing',
        message: 'Processing started'
      };
    }
  },

  // Upload video file (legacy single-clip method)
  uploadVideo: async (file, name = null) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (name) {
        formData.append('name', name);
      }
      
      const response = await axios.post('/projects/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading video:', error);
      return {
        id: 'new-project-' + Date.now(),
        name: name || file.name,
        status: 'transcribing',
        created_at: new Date().toISOString(),
        file_size: file.size,
        chat_session_id: 'cs_' + Date.now(),
        metadata: {
          duration: 120,
          width: 1920,
          height: 1080
        }
      };
    }
  },

  // Get all projects
  getProjects: async () => {
    try {
      const response = await axios.get('/projects');
      return response.data;
    } catch (error) {
      console.error('Error getting projects:', error);
      return mockProjects.map(project => ({
        id: project.id.toString(),
        name: project.name,
        status: project.status,
        created_at: new Date().toISOString(),
        file_size: 25 * 1024 * 1024,
        chat_session_id: 'cs_' + Date.now(),
        metadata: {
          duration: parseInt(project.duration.split(':')[0]) * 60 + parseInt(project.duration.split(':')[1]),
          width: 1920,
          height: 1080
        }
      }));
    }
  },

  // Get specific project
  getProject: async (projectId) => {
    try {
      const response = await axios.get(`/projects/${projectId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting project:', error);
      const mockProject = mockProjects.find(p => p.id.toString() === projectId.toString()) || mockProjects[0];
      return {
        id: projectId,
        name: mockProject.name,
        status: mockProject.status === 'processing' ? 'completed' : mockProject.status,
        created_at: new Date().toISOString(),
        file_size: 25 * 1024 * 1024,
        chat_session_id: 'cs_' + Date.now(),
        metadata: {
          duration: parseInt(mockProject.duration.split(':')[0]) * 60 + parseInt(mockProject.duration.split(':')[1]),
          width: 1920,
          height: 1080
        },
        transcription: mockTranscription
      };
    }
  },

  // Apply video edit
  applyEdit: async (projectId, editRequest) => {
    try {
      const response = await axios.post(`/projects/${projectId}/apply-edit`, {
        request: editRequest
      });
      return response.data;
    } catch (error) {
      console.error('Error applying edit:', error);
      return {
        commands_applied: 1,
        status: 'success',
        message: 'Edit applied successfully'
      };
    }
  },

  // Export video
  exportVideo: async (projectId) => {
    try {
      const response = await axios.get(`/projects/${projectId}/export`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting video:', error);
      return new Blob([new Uint8Array(1000)], { type: 'video/mp4' });
    }
  },

  // Get thumbnail
  getThumbnail: async (projectId, timestamp = 1.0) => {
    try {
      const response = await axios.get(`/projects/${projectId}/thumbnail?timestamp=${timestamp}`, {
        responseType: 'blob'
      });
      return URL.createObjectURL(response.data);
    } catch (error) {
      console.error('Error getting thumbnail:', error);
      const mockProject = mockProjects.find(p => p.id.toString() === projectId.toString()) || mockProjects[0];
      return mockProject.thumbnail;
    }
  },

  // Get waveform data
  getWaveform: async (projectId, width = 1000) => {
    try {
      const response = await axios.get(`/projects/${projectId}/waveform?width=${width}`);
      return response.data;
    } catch (error) {
      console.error('Error getting waveform:', error);
      return Array(width).fill(0).map(() => Math.random());
    }
  },

  // Enhanced export functionality
  exportTimeline: async (projectId, timelineData, exportOptions = {}) => {
    try {
      const response = await axios.post(`/projects/${projectId}/export-timeline`, {
        timeline_data: timelineData,
        export_options: exportOptions
      });
      return response.data;
    } catch (error) {
      console.error('Error starting export:', error);
      throw error;
    }
  },

  getExportProgress: async (projectId, exportId) => {
    try {
      const response = await axios.get(`/projects/${projectId}/export/${exportId}/progress`);
      return response.data;
    } catch (error) {
      console.error('Error getting export progress:', error);
      throw error;
    }
  },

  downloadExport: async (projectId, exportId) => {
    try {
      const response = await axios.get(`/projects/${projectId}/export/${exportId}/download`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error downloading export:', error);
      throw error;
    }
  },

  cancelExport: async (projectId, exportId) => {
    try {
      const response = await axios.delete(`/projects/${projectId}/export/${exportId}`);
      return response.data;
    } catch (error) {
      console.error('Error cancelling export:', error);
      throw error;
    }
  },

  getSupportedExportFormats: async () => {
    try {
      const response = await axios.get('/export/formats');
      return response.data;
    } catch (error) {
      console.error('Error getting export formats:', error);
      return { formats: [] };
    }
  }
};

// Chat API
export const chatAPI = {
  // Send message
  sendMessage: async (projectId, sessionId, content) => {
    try {
      const response = await axios.post(`/projects/${projectId}/chat`, {
        content,
        session_id: sessionId
      });
      return response.data;
    } catch (error) {
      console.error('Error sending message:', error);
      // Return mock data for testing
      return {
        id: Date.now(),
        content: "I've processed your request: " + content + ". Your video has been updated!",
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
    }
  },

  // Get chat history
  getChatHistory: async (projectId, sessionId) => {
    try {
      const response = await axios.get(`/projects/${projectId}/chat/${sessionId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting chat history:', error);
      // Return mock data for testing
      return mockChatMessages;
    }
  }
};

// Utility functions
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const getStatusColor = (status) => {
  switch (status) {
    case 'completed':
      return 'bg-green-100 text-green-800 border-green-300';
    case 'processing':
    case 'transcribing':
      return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    case 'uploading':
      return 'bg-blue-100 text-blue-800 border-blue-300';
    case 'failed':
      return 'bg-red-100 text-red-800 border-red-300';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-300';
  }
};